import { zyServer } from '@/lib/servers';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ZyMathWorksLearningToolNodeView from './zymathworks-learning-tool-node-view';

const createMatlabResource = async (): Promise<string> => {
    const response = await zyServer.post<{
        success: boolean;
        payload: {
            launch_url?: string;
            launch_data?: Record<string, unknown>;
            provider_name?: string;
            resource_link_id?: string;
        };
    }>('lti/matlab', { body: {} });
    return response.payload.resource_link_id || '';
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyMathWorksLearningTool: {
            insertZyMathWorksLearningTool: (options?: InsertZyMathWorksLearningToolOptions) => ReturnType;
        };
    }
}

export interface InsertZyMathWorksLearningToolOptions {
    id?: string;
    providerName?: string;
    caption?: string;
    ltiResourceLinkId?: string;
    parts?: number;
}

export const ZyMathWorksLearningToolNodeHeadless = Node.create({
    name: 'zyMathWorksLearningTool',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-tool-id'),
                renderHTML: attributes => ({
                    'data-tool-id': attributes.id,
                }),
            },
            providerName: {
                default: 'mathworks_learning_tool',
                parseHTML: element => element.getAttribute('data-provider-name') || 'mathworks_learning_tool',
                renderHTML: attributes => ({
                    'data-provider-name': attributes.providerName || 'mathworks_learning_tool',
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
            ltiResourceLinkId: {
                default: '',
                parseHTML: element => element.getAttribute('data-lti-resource-link-id') || '',
                renderHTML: attributes => ({
                    'data-lti-resource-link-id': attributes.ltiResourceLinkId,
                }),
            },
            parts: {
                default: 1,
                parseHTML: element => parseInt(element.getAttribute('data-parts') || '1', 10),
                renderHTML: attributes => ({
                    'data-parts': attributes.parts,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="zy-mathworks-learning-tool"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'zy-mathworks-learning-tool',
                class: 'zy-mathworks-learning-tool-container',
            }),
        ];
    },

    addCommands() {
        return {
            insertZyMathWorksLearningTool:
                (options: InsertZyMathWorksLearningToolOptions = {}) =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            id: options.id || null,
                            providerName: options.providerName || 'mathworks_learning_tool',
                            caption: options.caption || '',
                            ltiResourceLinkId: options.ltiResourceLinkId || '',
                            parts: options.parts || 1,
                        },
                    });
                },
        };
    },
});

export { createMatlabResource };

export const ZyMathWorksLearningToolNode = ZyMathWorksLearningToolNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyMathWorksLearningToolNodeView);
    },
});

export default ZyMathWorksLearningToolNode;
